/**
 * React Query hooks for chat session management.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listChatSessions,
  getChatSession,
  updateChatSession,
  deleteChatSession,
  type ChatSessionsListResponse,
  type ChatSessionDetail,
} from "../services/naraDoor";
import { getCurrentWorkspaceId, requireCurrentWorkspaceId } from "../lib/workspaceStore";

export function useChatSessions(params: { projectId?: string; scope?: string }) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<ChatSessionsListResponse>({
    queryKey: ["chatSessions", params.projectId, params.scope, workspaceId],
    queryFn: () => listChatSessions(workspaceId!, params),
    enabled: !!workspaceId,
  });
}

export function useChatSession(sessionId: string | null) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<ChatSessionDetail>({
    queryKey: ["chatSession", sessionId, workspaceId],
    queryFn: () => getChatSession(workspaceId!, sessionId!),
    enabled: !!sessionId && !!workspaceId,
  });
}

export function useRenameChatSession() {
  const queryClient = useQueryClient();

  return useMutation<{ id: string; title: string }, Error, { sessionId: string; title: string }>({
    mutationFn: ({ sessionId, title }) => updateChatSession(requireCurrentWorkspaceId(), sessionId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
    },
  });
}

export function useDeleteChatSession() {
  const queryClient = useQueryClient();

  return useMutation<{ status: string }, Error, { sessionId: string }>({
    mutationFn: ({ sessionId }) => deleteChatSession(requireCurrentWorkspaceId(), sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
    },
  });
}
